import { Tabs } from '@design-systems/canvas-ui'
import { MPMLayout } from 'components/layout'
import { PageTabs } from 'components/page-tabs'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useMemo } from 'react'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'
import { useLogClick } from 'utils/analytics'
import {
    Outlet,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import './operations.css'
import { UrlParams } from '../../enums/url-params'
import { BackButton } from '../../components/button/back-button'
import { OPERATIONS_TABS, getActiveOperationsTab } from './helpers'

export const OperationsPage = () => {
    const { t, params } = useEssentials()
    const [searchParams] = useSearchParams()
    const metricName = searchParams.get(UrlParams.METRIC_NAME)
    const heading = metricName || t(TRANSLATION_KEYS.operationalMetrics.title)
    const locationParams = params.get('locationId')
    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={heading}
                />
            )}
        >
            <Operations
                key={locationParams ?? ''}
                heading={heading}
                isMenuOverride={Boolean(metricName)}
            />
        </ErrorBoundary>
    )
}

export const Operations = ({
    heading,
    isMenuOverride,
}: {
    heading: string
    isMenuOverride: boolean
}) => {
    const { t } = useEssentials()
    const { operationalMetrics } = TRANSLATION_KEYS
    const { recall } = operationalMetrics
    const { tabs: tabsKeys } = recall
    const navigate = useNavigate()
    const location = useLocation()
    const tabsWithTranslations = useMemo(() => {
        return [
            { key: OPERATIONS_TABS.OPERATIONS, label: t(tabsKeys.operations) },
            {
                key: OPERATIONS_TABS.RECALL,
                label: t(tabsKeys.incompleteRecalls),
            },
        ]
    }, [t, tabsKeys])

    const activeTab = getActiveOperationsTab(location.pathname)

    const searchParams = useMemo(
        () => new URLSearchParams(window.location.search),
        [],
    )
    const { logClick } = useLogClick('operations')
    const handleTabSelect = (activeTab: string) => {
        logClick('operations' + activeTab)
        const path =
            activeTab === OPERATIONS_TABS.RECALL
                ? `/operations/${activeTab}`
                : '/operations'
        void navigate(`${path}?${searchParams.toString()}`, {
            replace: true,
        })
    }

    const hasTabs = tabsWithTranslations.length > 0

    return (
        <MPMLayout
            heading={heading}
            className="hc8-ph-none"
            isMenuOverride={isMenuOverride}
            menuOverride={<BackButton />}
            hasTabs={hasTabs}
            tabs={
                hasTabs && (
                    <PageTabs
                        activeTab={activeTab}
                        className="tabs hc8-pv-2x"
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {tabsWithTranslations.map((tab) => (
                            <Tabs.Item key={tab.key} name={tab.key}>
                                {tab.label}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                )
            }
        >
            <Outlet />
        </MPMLayout>
    )
}
